import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Store,
} from "lucide-react";
import {
  createWaBusinessGroup,
  updateWaBusinessGroup,
  deleteWaBusinessGroup,
} from "../../../../api/channelIntegrations";
import { showToast } from "../../../../utils/showToast";
import { apiBodyMessage } from "../../../../utils/apiErrorMessage";
import { cn } from "../../../../utils/cn";

export default function BusinessGroupManager({ groups, onRefresh }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", external_commerce_id: "" });
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setForm({ name: "", description: "", external_commerce_id: "" });
    setCreating(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast(t("El nombre del grupo es obligatorio"), false);
      return;
    }
    setSaving(true);
    if (editingId) {
      const result = await updateWaBusinessGroup(editingId, form);
      if (result?.ok) {
        showToast(t("Grupo actualizado"), true);
        resetForm();
        onRefresh?.();
      } else {
        // `result.error` is the full Nest body; the helper joins class-validator
        // arrays and drops bare status phrases.
        showToast(
          apiBodyMessage(result?.error) ?? t("Error al actualizar grupo"),
          false,
        );
      }
    } else {
      const result = await createWaBusinessGroup(
        form.name,
        form.description,
        form.external_commerce_id,
      );
      if (result?.ok) {
        showToast(t("Grupo creado"), true);
        resetForm();
        onRefresh?.();
      } else {
        showToast(
          apiBodyMessage(result?.error) ?? t("Error al crear grupo"),
          false,
        );
      }
    }
    setSaving(false);
  };

  const handleEdit = (group) => {
    setEditingId(group.id);
    setForm({
      name: group.name || "",
      description: group.description || "",
      external_commerce_id: group.external_commerce_id || "",
    });
    setCreating(true);
  };

  const handleDelete = async (groupId) => {
    // TODO: reemplazar window.confirm por modal coherente con DisconnectModal.
    // Por ahora se mantiene como antes para no salirme del scope de este pase.
    if (!window.confirm(t("¿Eliminar este grupo? Esta acción no se puede deshacer."))) {
      return;
    }
    const result = await deleteWaBusinessGroup(groupId);
    if (result?.ok) {
      showToast(t("Grupo eliminado"), true);
      onRefresh?.();
    } else {
      showToast(
        apiBodyMessage(result?.error) ?? t("Error al eliminar grupo"),
        false,
      );
    }
  };

  return (
    <div className="rounded-lg border border-border bg-bg overflow-hidden">
      {/* Toggle */}
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2 px-3 h-10 text-left text-sm font-medium text-fg hover:bg-bg-muted/40 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
        )}
        <Store className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
        <span className="flex-1">{t("Grupos de negocio")}</span>
        {groups?.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-bg-subtle text-fg-muted text-[11px] md:text-[10px] font-semibold">
            {groups.length}
          </span>
        )}
      </button>

      {expanded && (
        <div className="border-t border-border p-3 space-y-3 bg-bg-muted/20">
          <p className="text-[12px] md:text-[11px] text-fg-muted leading-relaxed">
            {t("Agrupa varios números de WhatsApp bajo un mismo comercio para enrutar los mensajes a la cuenta correcta.")}
          </p>

          {/* Lista de grupos */}
          {groups?.length > 0 && (
            <div className="space-y-1.5">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-start justify-between gap-3 px-3 py-2 rounded-md border border-border bg-bg"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-fg truncate">{group.name}</div>
                    {group.description && (
                      <div className="text-[12px] md:text-[11px] text-fg-muted truncate mt-0.5">
                        {group.description}
                      </div>
                    )}
                    <div className="text-[11px] md:text-[10px] text-fg-subtle mt-0.5">
                      {group.instance_count || 0} {t("número(s)")}
                      {group.external_commerce_id && (
                        <span className="font-mono"> · {t("ID")}: {group.external_commerce_id}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleEdit(group)}
                      title={t("Editar")}
                      aria-label={t("Editar grupo")}
                      className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(group.id)}
                      title={t("Eliminar")}
                      aria-label={t("Eliminar grupo")}
                      className="inline-flex w-7 h-7 items-center justify-center rounded-md text-fg-muted hover:bg-status-error-soft hover:text-status-error transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Formulario crear/editar */}
          {creating ? (
            <div className="space-y-2 p-3 rounded-md border border-border bg-bg">
              <div className="text-[11px] md:text-[10px] uppercase tracking-wider font-semibold text-fg-subtle">
                {editingId ? t("Editar grupo") : t("Nuevo grupo")}
              </div>
              <input
                type="text"
                placeholder={t("Nombre del grupo (ej. Tienda Central)")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
                className="w-full h-8 px-2.5 rounded-md border border-border bg-bg text-xs text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <input
                type="text"
                placeholder={t("Descripción (opcional)")}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full h-8 px-2.5 rounded-md border border-border bg-bg text-xs text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <input
                type="text"
                placeholder={t("ID externo del comercio (opcional)")}
                value={form.external_commerce_id}
                onChange={(e) => setForm({ ...form, external_commerce_id: e.target.value })}
                className="w-full h-8 px-2.5 rounded-md border border-border bg-bg text-xs text-fg placeholder:text-fg-subtle font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-medium text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50 transition-colors"
                >
                  <X className="w-3 h-3" aria-hidden="true" />
                  {t("Cancelar")}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!form.name.trim() || saving}
                  className="hd-brand-solid inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="w-3 h-3" aria-hidden="true" />
                  {saving ? t("Guardando…") : editingId ? t("Actualizar") : t("Crear")}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="w-full inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border border-dashed border-border text-sm font-medium text-fg-muted hover:border-brand-500 hover:text-brand-700 hover:bg-brand-50/40 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              {t("Nuevo grupo")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
